import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import { useClinic } from '@/hooks/useClinicSettings';
import {
  isClinicModuleEnabled,
  readActivatedModules,
} from '@/lib/clinicModules';
import type { PermissionAction, PermissionResource } from '@/lib/permissions';
import { NoAccess } from '@/components/NoAccess';

interface PermissionRouteProps {
  /** Single resource (legacy) or any-of list. */
  resource?: PermissionResource;
  anyOf?: PermissionResource[];
  /** Extra resource that must also be granted (typically a nav_* item). */
  nav?: PermissionResource;
  action?: PermissionAction;
  /** Optional clinic module that must be enabled (see `activated_modules`). */
  moduleKey?: string;
  children: React.ReactNode;
}

/** Blocks route content when the active clinic membership lacks permission. */
export function PermissionRoute({
  resource,
  anyOf,
  nav,
  action = 'view',
  moduleKey,
  children,
}: PermissionRouteProps) {
  const { hasPermission, status } = useAuth();
  const { activeClinicId } = useActiveClinic();
  const { data: clinic, isLoading: clinicLoading } = useClinic(
    moduleKey ? activeClinicId : null,
  );

  if (status === 'initializing') return null;
  if (moduleKey && clinicLoading) return null;

  if (moduleKey) {
    const modules = readActivatedModules(clinic?.activated_modules);
    if (!isClinicModuleEnabled(modules, moduleKey)) {
      return <NoAccess />;
    }
  }

  if (nav && !hasPermission(activeClinicId, nav, 'view')) {
    return <NoAccess />;
  }

  const resources = anyOf ?? (resource ? [resource] : []);
  const allowed = resources.length === 0 || resources.some((r) => hasPermission(activeClinicId, r, action));

  if (!allowed) {
    return <NoAccess />;
  }

  return <>{children}</>;
}
